import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { fetchContext, homeFor, StaffContext } from '../lib/staffContext';
import { AuthFrame } from '../ui/AuthFrame';
import { Pill } from '../ui/DataBits';
import { ContextError } from '../ui/ContextError';
import { useI18n } from '../i18n';
import { errorText, rpc, type AppError } from './admin/api';

// I5: the staff notice, shown once at first sign-in (and again when it
// changes) for every school-side role and municipality coordinators: what is
// stored about you, who sees what, and how to have it removed. Reading it is
// recorded (audit log); the own check-in has its own, separate consent.
export function StaffNotice() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [ctx, setCtx] = useState<StaffContext | null>(null);
  const [fatal, setFatal] = useState<string | null>(null);
  const [err, setErr] = useState<AppError | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetchContext().then((r) => {
      if (r.kind === 'signed_out') navigate('/teacher/login', { replace: true });
      else if (r.kind === 'no_access') navigate('/teacher/login?reason=no_access', { replace: true });
      else if (r.kind === 'unavailable') setFatal(r.message);
      else if (!r.ctx.notice_required) navigate(homeFor(r.ctx.active_role), { replace: true });
      else setCtx(r.ctx);
    });
  }, [navigate]);

  async function accept() {
    if (!ctx) return;
    setBusy(true);
    setErr(null);
    const r = await rpc('acknowledge_staff_notice', { p_version: ctx.notice_version ?? 1 });
    setBusy(false);
    if (r.error) return setErr(r.error);
    navigate(homeFor(ctx.active_role), { replace: true });
  }
  async function signOut() {
    await supabase.auth.signOut();
    navigate('/teacher/login');
  }

  if (fatal) return <ContextError message={fatal} />;
  if (!ctx) return <main className="page-loading" role="status">{t.loading}</main>;

  const sections: [string, string][] = [
    [t['r3.notice.storedTitle'], t['r3.notice.stored']],
    [t['r3.notice.whoTitle'], t['r3.notice.who']],
    [t['r3.notice.studentsTitle'], t['r3.notice.students']],
    [t['r3.notice.checkinTitle'], t['r3.notice.checkin']],
    [t['r3.notice.removeTitle'], t['r3.notice.remove']],
    [t['r3.notice.keepTitle'], t['r3.notice.keep']],
  ];
  return (
    <AuthFrame title={t['r3.notice.title']} wide>
      <p><Pill kind="warn">{t['ts.draft']}</Pill> {t['r3.notice.intro']}</p>
      {sections.map(([h, b]) => (
        <section key={h} className="notice-section">
          <h2 className="overline">{h}</h2>
          <p>{b}</p>
        </section>
      ))}
      <p className="form-hint">{t['r3.notice.more']} <a href="/trust">{t['r3.notice.trustLink']}</a> · <a href="/privacy-method">{t['r3.method.title']}</a></p>
      {err && <p className="error-text" role="alert">{errorText(t, err)}</p>}
      <div className="form-actions">
        <button type="button" className="btn btn-ghost" onClick={signOut}>{t.signOut}</button>
        <button type="button" className="btn btn-primary" onClick={accept} disabled={busy}>{t['r3.notice.accept']}</button>
      </div>
    </AuthFrame>
  );
}
